<script setup>
    const { signOut, isLoggedIn } = useAuth()
    
    const props = defineProps({
        type: {
            type: String,
            required: false,
            default: 'sites',
        },
        /**
         * Lifts the alert out of flow to sit ON TOP of a map — the only case
         * that needs it. Everywhere else it stays in flow so its container
         * actually grows to hold it (Asana O2-48).
         */
        overlay: {
            type: Boolean,
            required: false,
            default: false,
        },
    });
</script>

<template>
    <div class="turquoise-alert" :class="{ overlay }">
        <div v-if="type === 'sites'">
            <SvgsLineMarker />
            <h2 class="turquoise-alert-headline">No sites have been favorited.</h2>
            <p><NuxtLink to="/sites" class="turquoise-alert-copy">Start adding sites to your favorites</NuxtLink></p>
            <p class="login" v-if="!isLoggedIn">
                <NuxtLink to="/login" class="turquoise-alert-copy">Log in to My OHC</NuxtLink> <strong>before</strong> saving sites or itineraries to access them across devices. If you don’t see your favorites, make sure you’re logged in here and on the device where you first saved them.
            </p>
            <p class="login" v-else>
                <strong>You’re logged in to My OHC</strong>. Any sites or itineraries you save here will sync across devices. If you don’t see items saved elsewhere, be sure you were logged in on that device when you saved them. <a href="#" @click.prevent="signOut" class="turquoise-alert-copy">Log out</a>.
            </p>
        </div>
        <div v-if="type === 'events'">
            <SvgsLineMarker />
            <h2 class="turquoise-alert-headline">No events have been favorited.</h2>
            <NuxtLink to="/events" class="turquoise-alert-copy">Start adding events to your favorites</NuxtLink>
            <p class="login" v-if="!isLoggedIn">
                <NuxtLink to="/login" class="turquoise-alert-copy">Log in to My OHC</NuxtLink> <strong>before</strong> saving events to access them across devices. If you don’t see your favorites, make sure you’re logged in here and on the device where you first saved them.
            </p>
            <p class="login" v-else>
                <strong>You’re logged in to My OHC</strong>. Any events you save here will sync across devices. If you don’t see items saved elsewhere, be sure you were logged in on that device when you saved them. <a href="#" @click.prevent="signOut" class="turquoise-alert-copy">Log out</a>.
            </p>
        </div>
        <div v-if="type === 'tours'">
            <SvgsLineMarker />
            <h2 class="turquoise-alert-headline">No itineraries have been favorited.</h2>
            <p><NuxtLink to="/itineraries" class="turquoise-alert-copy">Start adding itineraries to your favorites</NuxtLink></p>
            <p class="login" v-if="!isLoggedIn">
                <NuxtLink to="/login" class="turquoise-alert-copy">Log in to My OHC</NuxtLink> <strong>before</strong> saving sites or itineraries to access them across devices. If you don’t see your favorites, make sure you’re logged in here and on the device where you first saved them.
            </p>
            <p class="login" v-else>
                <strong>You’re logged in to My OHC</strong>. Any sites or itineraries you save here will sync across devices. If you don’t see items saved elsewhere, be sure you were logged in on that device when you saved them. <a href="#" @click.prevent="signOut" class="turquoise-alert-copy">Log out</a>.
            </p>
        </div>
    </div>
</template>

<style lang="scss">
    .turquoise-alert {
        z-index: 2;
        // IN FLOW by default. This was `position: absolute` everywhere, which
        // meant it contributed no height at all: on My OHC the empty state is
        // the only thing on the page, so the page fell back to its 400px
        // min-height while the alert needed ~290px from the top of a container
        // that was 30px tall. The overflow ran straight into the footer and
        // clipped the alert's own last line (Asana O2-48). It got worse as the
        // viewport narrowed and the copy wrapped — 7px at 1763px wide, 46px at
        // 604px — so a fixed footer margin would only have hidden it at the one
        // width it was measured on.
        margin: 45px 15px;
        padding: 30px;
        background-color: rgba($turquoise, 0.95);
        text-align: center;

        // Over a map, where there IS something to sit on top of and the map
        // supplies its own height. Insets match the previous absolute values.
        &.overlay {
            position: absolute;
            top: 45px;
            right: 15px;
            left: 15px;
            margin: 0;
        }

        .line-marker {
            margin-bottom: 15px;
        }

        .login {
            a {
                text-decoration: underline;
                font-size: 1em;

                &:hover {
                    color: white;
                    text-decoration: none;
                }
            }
        }

        p {
            margin-bottom: 16px;

            &.login {
                margin-top: 32px;
                font-size: 0.85em;
            }
        }

        .turquoise-alert-headline {
            margin-bottom: 10px;
        }

        @media (min-width: $tablet) {
            // 75px = the 15px inset plus the 60px margin the overlay stacked on
            // top of it, so the in-flow width matches what shipped before.
            margin: 45px 75px;

            &.overlay {
                margin: 0 60px;
            }

            .turquoise-alert-headline {
                font-size: 24px;
            }
        }
    }
</style>
